import { Node, mergeAttributes } from "@tiptap/core";

import { hasValue } from "@/Utils/Objects";
import { EditorMode, type CustomInputOptions } from "../interfaces";
import { FREE_CUSTOM_INPUT_VALUE } from "../assets";
import { isValidString } from "@/Utils/String";

interface insertCustomInputOptions {
  label?: string;
  type?: string;
  name?: string;
  placeholder?: string;
  value?: string;
}
declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    CustomInput: {
      insertCustomInput: (op: insertCustomInputOptions) => ReturnType;
    };
  }
}

const canComplete = (opts: { name: string; type: string; value: string }) => {
  if (opts.name.startsWith(FREE_CUSTOM_INPUT_VALUE)) return true;
  if (!isValidString(opts.value) && ["user.", "client."].some((prefix) => opts.name.startsWith(prefix))) {
    return true;
  }

  return false;
};

const CustomInput = Node.create<CustomInputOptions>({
  name: "CustomInput",

  inline: true,
  group: "inline",
  atom: true,
  content: "",
  selectable: true,
  draggable: false,

  addOptions() {
    return {
      mode: EditorMode.EDITOR,
      onChange: undefined,
    };
  },

  addAttributes() {
    return {
      type: { default: "text" },
      name: { default: "input-name" },
      value: { default: "" },
      placeholder: { default: undefined },
    };
  },

  renderHTML({ HTMLAttributes }) {
    // Solo como fallback si no se usa addNodeView
    const { type, name, placeholder, value, ...rest } = HTMLAttributes;

    return [
      "span",
      mergeAttributes(rest, { class: "html_doc__input_field", contenteditable: "false" }),
      [
        "input",
        {
          type,
          name,
          placeholder,
          value,
        },
      ],
    ];
  },

  parseHTML() {
    return [
      {
        tag: ".html_doc__input_field",
        getAttrs: (el) => {
          const input = (el as HTMLElement).querySelector("input");

          return hasValue(input)
            ? {
                type: input.getAttribute("type") ?? "text",
                name: input.getAttribute("name") ?? "input-name",
                value: input.getAttribute("value") ?? "",
                placeholder: input.hasAttribute("placeholder")
                  ? (input.getAttribute("placeholder") ?? undefined)
                  : undefined,
              }
            : null;
        },
      },
    ];
  },

  addCommands() {
    return {
      insertCustomInput:
        (options: insertCustomInputOptions = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              type: options.type || "text",
              name: options.name || "input-name",
              value: options.value || "",
              placeholder: options.placeholder || "Escribe el placeholder aquí...",
            },
          });
        },
    };
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const outer = document.createElement("span");
      const input = document.createElement("input");
      const isCompleteMode =
        this.options.mode === EditorMode.COMPLETE &&
        canComplete({
          name: node.attrs.name ?? "",
          type: node.attrs.type ?? "",
          value: node.attrs.value ?? "",
        });

      const stopKeys = (ev: KeyboardEvent) => {
        ev.stopPropagation();
      };

      const onChange = () => {
        const pos = typeof getPos === "function" ? getPos() : -1;

        if (!hasValue(pos) || pos < 0) return;

        const next = input.value ?? "";

        if (next === node.attrs.value) return;

        const tr = editor.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, value: next });

        editor.view.dispatch(tr);
        this.options.onChange?.({ name: node.attrs.name, value: next });
      };

      outer.classList.add("html_doc__input_field");

      if (node.attrs.placeholder) input.placeholder = node.attrs.placeholder;
      if (hasValue(node.attrs.value)) input.value = node.attrs.value;

      input.type = node.attrs.type;
      input.name = node.attrs.name;

      if (!isCompleteMode) {
        input.readOnly = true;
        input.tabIndex = -1;
        input.disabled = true;
      } else {
        input.addEventListener("keydown", stopKeys);
        input.addEventListener("input", onChange);
        input.addEventListener("change", onChange);
      }

      outer.appendChild(input);

      return {
        dom: outer,
        update: (updatedNode) => {
          if (updatedNode.type.name !== node.type.name) return false;

          if (updatedNode.attrs.value !== input.value) {
            input.value = updatedNode.attrs.value ?? "";
          }

          if (updatedNode.attrs.placeholder !== node.attrs.placeholder) {
            input.placeholder = updatedNode.attrs.placeholder ?? "";
          }

          return true;
        },

        ignoreMutation: () => true,
        destroy: () => {
          input.removeEventListener("keydown", stopKeys);
          input.removeEventListener("input", onChange);
          input.removeEventListener("change", onChange);
        },
      };
    };
  },
});

export default CustomInput;
